import type {
  DeepKeys,
  DeepValue,
  FieldApi,
  Updater,
} from "@tanstack/react-form";
import type { z } from "zod";

import LabeledField from "@/components/form/labeled-field";
import type { SelectCustomProps } from "@/components/select-custom";
import { Select } from "@/components/select-custom";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";
import type { Risk } from "@/utils/patient";

type getOptionsType<TFormData, TName extends DeepKeys<TFormData>> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    TFormData,
    TName,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >
) => SelectCustomProps["options"];

export type FieldSelectProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = FieldBaseProps<TFormData, TName, TSchema, TData> & {
  type?: "string" | "number";
  risk?: Risk | "tooth";
  isMotive?: boolean;
} & (
    | (Omit<SelectCustomProps, "value" | "options"> & {
        getOptions: getOptionsType<TFormData, TName>;
      })
    | Omit<SelectCustomProps, "value">
  );

const FieldSelect = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  labelProps,
  onValueChange,
  type,
  risk,
  isMotive = false,
  validators,
  gridRow,
  ...props
}: FieldSelectProps<TFormData, TName, TSchema, TData>) => {
  const getOptions = (
    props as { getOptions?: getOptionsType<TFormData, TName> }
  ).getOptions;
  const options = (
    props as { options: SelectCustomProps["options"] | undefined }
  ).options;

  const hasEmptyOption = options?.some((o) => o.value === "") ?? false;

  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => (
        <LabeledField
          label={label}
          fieldName={field.name.toString()}
          errors={field.state.meta.errors}
          required={props.required}
          gridRow={gridRow}
          fullWidth={!risk}
          {...labelProps}
        >
          <Select
            value={(field.state.value as string | null)?.toString() ?? ""}
            onValueChange={(value) => {
              // hot fix: value change on page load (see https://trello.com/c/TXavh4JE)
              // on some page load, onValueChange is triggered without reason
              if (value === "" && !hasEmptyOption) return;

              if (onValueChange) onValueChange(value);
              (
                field.handleChange as (
                  updater: Updater<string | number | null>
                ) => void
              )(type === "number" ? (value !== "" ? +value : null) : value);
            }}
            options={getOptions ? getOptions(field) : options!}
            risk={risk}
            isMotive={isMotive}
            {...props}
          />
        </LabeledField>
      )}
    />
  );
};

export default FieldSelect;
